import React, { useState } from 'react';
import { motion } from 'framer-motion';

const rawApiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
const envApiBaseUrl = rawApiBaseUrl.replace(/\/+$/, '');

function resolveApiBaseUrl() {
  if (envApiBaseUrl) return envApiBaseUrl;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host === 'bonkmusic.qzz.io') {
      return 'https://bonksite-production.schitturi1.workers.dev';
    }
  }
  return '';
}

const apiBaseUrl = resolveApiBaseUrl();

function buildApiUrl(pathname) {
  if (!pathname.startsWith('/')) {
    throw new Error(`Expected absolute API path, got: ${pathname}`);
  }
  return apiBaseUrl ? `${apiBaseUrl}${pathname}` : pathname;
}

const cardStyle = {
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 14,
  background: '#09090B',
  padding: 24,
};

const inputStyle = {
  width: '100%',
  background: '#0F0F11',
  color: '#E4E4E7',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  padding: '11px 12px',
  fontSize: 14,
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  marginBottom: 8,
  fontSize: 12,
  letterSpacing: '0.03em',
  color: '#A1A1AA',
  fontWeight: 600,
};

async function readResponseError(response, fallbackMessage) {
  try {
    const data = await response.json();
    if (data && typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
  } catch {
    // Ignore parse errors and keep fallback message.
  }
  return fallbackMessage;
}

function WaitlistForm() {
  const [form, setForm] = useState({ email: '', name: '', role: '', notes: '' });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const source = 'bonk-site-refresh';

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(buildApiUrl('/api/waitlist'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role.trim(),
          notes: form.notes.trim(),
          source,
        }),
      });

      if (!response.ok) {
        const serverError = await readResponseError(response, 'Could not submit waitlist request. Please try again.');
        throw new Error(serverError);
      }

      setStatus('success');
      setMessage('You are on the waitlist.');
      setForm({ email: '', name: '', role: '', notes: '' });
    } catch (error) {
      setStatus('error');
      setMessage(error?.message || 'Could not submit waitlist request. Please try again.');
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ fontFamily: 'var(--heading)', marginBottom: 8, fontSize: 20 }}>Join the waitlist</h3>
      <p style={{ color: '#71717A', fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>
        Get release notes and feature rollouts for Bonk site updates.
      </p>
      <form onSubmit={onSubmit}>
        <label style={labelStyle} htmlFor="waitlist-email">Email</label>
        <input
          id="waitlist-email"
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          placeholder="you@example.com"
          required
          style={inputStyle}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="waitlist-name">Name (optional)</label>
            <input
              id="waitlist-name"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Your name"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="waitlist-role">Role (optional)</label>
            <select
              id="waitlist-role"
              name="role"
              value={form.role}
              onChange={onChange}
              style={inputStyle}
            >
              <option value="">Select role (optional)</option>
              <option value="dj">DJ</option>
              <option value="producer">Producer</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <label style={{ ...labelStyle, marginTop: 12 }} htmlFor="waitlist-notes">Notes (optional)</label>
        <textarea
          id="waitlist-notes"
          name="notes"
          value={form.notes}
          onChange={onChange}
          rows={3}
          placeholder="Anything we should know?"
          style={{ ...inputStyle, resize: 'vertical', minHeight: 84 }}
        />

        <input type="hidden" name="source" value={source} />
        <button
          type="submit"
          disabled={status === 'loading'}
          style={{
            marginTop: 14,
            background: '#FAFAFA',
            color: '#09090B',
            border: 'none',
            borderRadius: 10,
            fontWeight: 700,
            padding: '11px 16px',
            width: '100%',
            cursor: status === 'loading' ? 'not-allowed' : 'pointer',
            opacity: status === 'loading' ? 0.7 : 1,
          }}
        >
          {status === 'loading' ? 'Submitting...' : 'Join waitlist'}
        </button>
      </form>
      {message ? (
        <p
          role="status"
          aria-live="polite"
          style={{ marginTop: 12, fontSize: 13, color: status === 'error' ? '#F87171' : '#34D399' }}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', topic: 'General inquiry', message: '' });
  const [status, setStatus] = useState('idle');
  const [responseMessage, setResponseMessage] = useState('');
  const source = 'bonk-site-refresh';

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setResponseMessage('');

    try {
      const response = await fetch(buildApiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          topic: form.topic.trim(),
          message: form.message.trim(),
          source,
        }),
      });

      if (!response.ok) {
        const serverError = await readResponseError(response, 'Could not send message. Please try again.');
        throw new Error(serverError);
      }

      setStatus('success');
      setResponseMessage('Message sent. We will reply soon.');
      setForm({ name: '', email: '', topic: 'General inquiry', message: '' });
    } catch (error) {
      setStatus('error');
      setResponseMessage(error?.message || 'Could not send message. Please try again.');
    }
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ fontFamily: 'var(--heading)', marginBottom: 8, fontSize: 20 }}>Contact the team</h3>
      <p style={{ color: '#71717A', fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>
        Questions about Bonk workflows, roadmap, or partnerships.
      </p>
      <form onSubmit={onSubmit}>
        <label style={labelStyle} htmlFor="contact-name">Name</label>
        <input id="contact-name" name="name" value={form.name} onChange={onChange} required style={inputStyle} />

        <label style={{ ...labelStyle, marginTop: 12 }} htmlFor="contact-email">Email</label>
        <input id="contact-email" type="email" name="email" value={form.email} onChange={onChange} required style={inputStyle} />

        <label style={{ ...labelStyle, marginTop: 12 }} htmlFor="contact-topic">Topic</label>
        <input
          id="contact-topic"
          name="topic"
          value={form.topic}
          onChange={onChange}
          required
          placeholder="General inquiry"
          style={inputStyle}
        />

        <label style={{ ...labelStyle, marginTop: 12 }} htmlFor="contact-message">Message</label>
        <textarea
          id="contact-message"
          name="message"
          value={form.message}
          onChange={onChange}
          required
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
        />

        <input type="hidden" name="source" value={source} />
        <button
          type="submit"
          disabled={status === 'loading'}
          style={{
            marginTop: 14,
            background: 'transparent',
            color: '#FAFAFA',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 10,
            fontWeight: 700,
            padding: '11px 16px',
            width: '100%',
            cursor: status === 'loading' ? 'not-allowed' : 'pointer',
            opacity: status === 'loading' ? 0.7 : 1,
          }}
        >
          {status === 'loading' ? 'Sending...' : 'Send message'}
        </button>
      </form>
      {responseMessage ? (
        <p
          role="status"
          aria-live="polite"
          style={{ marginTop: 12, fontSize: 13, color: status === 'error' ? '#F87171' : '#34D399' }}
        >
          {responseMessage}
        </p>
      ) : null}
    </div>
  );
}

export default function LeadCapture() {
  return (
    <section id="contact" style={{ padding: 'clamp(80px, 10vw, 140px) 0', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: 650, margin: '0 auto 42px' }}
        >
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, color: '#52525B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Stay in the loop
          </span>
          <h2 style={{ fontFamily: 'var(--heading)', fontWeight: 800, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', marginTop: 16 }}>
            Join waitlist or contact us
          </h2>
          <p style={{ color: '#71717A', marginTop: 16, lineHeight: 1.7 }}>
            Use these forms for release updates, feature announcements, or direct questions.
          </p>
        </motion.div>

        <div className="lead-capture-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <WaitlistForm />
          <ContactForm />
        </div>
      </div>
      <style>{`@media(max-width:900px){.lead-capture-grid{grid-template-columns:1fr !important}}`}</style>
    </section>
  );
}
